import { AccountType } from "@common/constants/domains/accounting/accounts";
import { AccountRole } from "@common/types/domains/accounting/account";
import { MatchItemType } from "./types";

export enum AccrualMatchSearchMode {
    ALL = "All",
    SUGGESTED = "Suggested",
}

interface AccrualMatchDrawerDefaults {
    defaultType: MatchItemType;
    defaultSearchMode: AccrualMatchSearchMode;
}

interface JournalAccount {
    type?: AccountType | null;
    role?: AccountRole | null;
}

export interface SearchMatchTypeOption {
    value: MatchItemType;
    label: string;
}

interface SearchMatchTypeOptionsParams {
    transactionAmount: number;
    hasApReconciliation: boolean;
    /**
     * Cash-only entities (no accrual ledger) are the only ones the backend
     * gates on `AP_RECONCILIATION`; the accrual paths gate bill matching
     * implicitly through `ACCRUAL_LEDGER`. Mirroring that here keeps an
     * entity that kept its accrual ledger after a plan downgrade able to
     * match the bills the approve endpoint still accepts.
     */
    isCashBasis: boolean;
    /**
     * `false` when the transaction can never be matched to a document — a
     * cash-basis transaction whose GL account lives on a different entity than
     * the one that would own the documents, since the backend only accepts
     * documents of `transaction.entityId`. Offering an Invoice/Bill tab there
     * produces a search that can never return a matchable row.
     */
    canMatchDocuments?: boolean;
    /**
     * Whether the workspace has ever had any invoices at all. A workspace
     * with zero invoices can never produce an Invoice match, so the tab is
     * pointless to offer even when the amount sign/ledger basis would
     * otherwise allow it. Defaults to `true` (permissive) so callers that
     * haven't wired the has-invoices/has-bills check yet, or are still
     * resolving it, keep the pre-existing behavior — see the `isChecking`
     * handling in `AccrualMatchPage` for why the loading state also stays
     * permissive.
     */
    hasInvoices?: boolean;
    /**
     * Same as `hasInvoices`, for bills. Defaults to `true` for the same
     * reason.
     */
    hasBills?: boolean;
}

interface GetAccrualMatchDrawerDefaultsParams {
    journalAccount?: JournalAccount | null;
    transactionAmount: number;
    searchMatchTypeOptions: SearchMatchTypeOption[];
}

/**
 * Invoices (AR) can only settle money-in, bills (AP) only money-out. The
 * backend rejects the other combination, so the drawer never offers it.
 */
export function getAllowedDocumentType(
    transactionAmount: number,
): MatchItemType {
    return transactionAmount > 0 ? MatchItemType.INVOICE : MatchItemType.BILL;
}

/**
 * Whether the transaction's effective journal account is an accrual-ledger
 * AR/AP account. Which document type that is follows from the amount sign
 * (`getAllowedDocumentType`), not from the role — a refund or a reversal can
 * post the opposite direction against the same account.
 */
export function isAccrualLedgerDocumentAccount(
    journalAccount: JournalAccount | null | undefined,
): boolean {
    return (
        journalAccount?.role === AccountRole.accountsReceivable ||
        journalAccount?.role === AccountRole.accountsPayable
    );
}

/**
 * Which document type a cash-ledger account implies, if any. Unlike the
 * accrual-ledger AR/AP case, the cash-ledger account itself pins the
 * document type directly — income (or its Uncategorized Income purpose)
 * always means Invoice, operating/other expenses (or its Uncategorized
 * Expenses purpose) always means Bill. COGS is deliberately excluded even
 * though it shares the Expenses account class — a COGS cash ledger doesn't
 * imply a bill match the way Operating/Other Expenses do.
 */
export function getCashLedgerDocumentType(
    journalAccount: JournalAccount | null | undefined,
): MatchItemType.INVOICE | MatchItemType.BILL | null {
    if (
        journalAccount?.type === AccountType.income ||
        journalAccount?.type === AccountType.otherIncome ||
        journalAccount?.role === AccountRole.uncategorizedIncome
    ) {
        return MatchItemType.INVOICE;
    }

    if (
        journalAccount?.type === AccountType.operatingExpenses ||
        journalAccount?.type === AccountType.otherExpenses ||
        journalAccount?.role === AccountRole.uncategorizedExpense
    ) {
        return MatchItemType.BILL;
    }

    return null;
}

/**
 * Which tabs the type selector should offer, layering two independent
 * gates on top of the amount-sign/ledger-basis rules: `canMatchDocuments`
 * (can a document of this transaction's entity ever match at all) and
 * `hasInvoices`/`hasBills` (does the workspace have any documents of that
 * type to match against). Both the amount-sign/ledger-basis condition AND
 * the corresponding has-invoices/has-bills flag must hold before a document
 * tab is offered — a workspace that has never created an invoice should not
 * see an Invoice tab even on an otherwise-eligible incoming transaction.
 */
export function getSearchMatchTypeOptions({
    transactionAmount,
    hasApReconciliation,
    isCashBasis,
    canMatchDocuments = true,
    hasInvoices = true,
    hasBills = true,
}: SearchMatchTypeOptionsParams): SearchMatchTypeOption[] {
    const options: SearchMatchTypeOption[] = [
        { value: MatchItemType.TRANSACTION, label: "Transaction" },
    ];

    if (!canMatchDocuments) {
        return options;
    }

    if (
        getAllowedDocumentType(transactionAmount) === MatchItemType.INVOICE &&
        hasInvoices
    ) {
        options.push({ value: MatchItemType.INVOICE, label: "Invoice" });
    } else if (
        getAllowedDocumentType(transactionAmount) === MatchItemType.BILL &&
        (!isCashBasis || hasApReconciliation) &&
        hasBills
    ) {
        options.push({ value: MatchItemType.BILL, label: "Bill" });
    }

    return options;
}

export function getAccrualMatchDrawerDefaults({
    journalAccount,
    transactionAmount,
    searchMatchTypeOptions,
}: GetAccrualMatchDrawerDefaultsParams): AccrualMatchDrawerDefaults {
    const resolvedType = isAccrualLedgerDocumentAccount(journalAccount)
        ? getAllowedDocumentType(transactionAmount)
        : (getCashLedgerDocumentType(journalAccount) ??
          MatchItemType.TRANSACTION);

    return {
        defaultType: clampToOfferedType(resolvedType, searchMatchTypeOptions),
        defaultSearchMode: AccrualMatchSearchMode.SUGGESTED,
    };
}

/**
 * Keeps a match type inside the set the type selector actually offers. The
 * offered set narrows asynchronously (billing entitlement and ledger basis
 * both resolve after mount), so a type that was valid a render ago can stop
 * being valid — leaving the user on a tab with no control to leave it.
 */
export function clampToOfferedType(
    type: MatchItemType,
    searchMatchTypeOptions: SearchMatchTypeOption[],
): MatchItemType {
    return searchMatchTypeOptions.some((option) => option.value === type)
        ? type
        : MatchItemType.TRANSACTION;
}
